import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Textarea, Select, Card, ModalShell, Pill } from '../lib/ui';
import { MAX_FEATURED_PRODUCTS, FALLBACK_PHOTOS, photosTableHtml, useFeaturablePhotos } from '../lib/productPhotos';
import { useSavedTemplate } from '../lib/useSavedTemplate';

// Same reply-to as the Outreach pitch — replies go to the mailbox Michael
// actually reads, not the general site contact address.
const REPLY_TO_EMAIL = 'mwt@teardrophydroponics.co.uk';
const MARKET_TEMPLATE_NAME = 'Markets starter template';
const DEFAULT_MARKET_SUBJECT = 'Stallholder enquiry — small local grower';

function buildMarketPitchHtml(photos) {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  body { font-family: "DM Sans", Helvetica Neue, Helvetica, Arial, sans-serif; background: #f5f7f5; margin: 0; padding: 20px 0; color: #111a14; }
  .wrap { max-width: 540px; margin: 0 auto; background: #fff; border-radius: 12px; overflow: hidden; border: 1px solid #d4e0d4; }
  .bd { padding: 32px 36px; }
  p { font-size: 15px; line-height: 1.7; color: #2a3b32; margin: 0 0 16px; }
  .photos { width: 100%; border-collapse: separate; border-spacing: 6px 0; margin: 4px 0 6px; }
  .photos td { width: 33.33%; }
  .photos img { width: 100%; height: 100px; object-fit: cover; border-radius: 8px; display: block; }
  .caption { font-size: 12px; color: #5e7265; margin: 0 0 20px; }
  .sig { margin-top: 22px; font-size: 15px; color: #111a14; line-height: 1.6; }
</style>
</head>
<body>
  <div class="wrap">
    <div class="bd">
      <p>Hi {{name}},</p>

      <p>I'm Michael — I run a small hydroponic farm just outside Hockley, growing herbs, salad leaves and microgreens indoors, year-round. I'm looking to start doing some market stalls and wondered whether you might have space for a new trader.</p>

      ${photosTableHtml(photos.length ? photos : FALLBACK_PHOTOS)}
      <p class="caption">A few things I grow.</p>

      <p>Everything's pesticide-free and cut fresh, and I'm a registered primary producer with a 5-star food hygiene rating, if that's useful to know.</p>

      <p>Would you be able to let me know if there's a pitch available, roughly what it costs, and what the application process looks like? Happy to send over more detail or photos if it helps.</p>

      <div class="sig">
        Thanks very much,<br />
        Michael<br />
        07541 264513<br />
        TearDrop Hydro, Hockley
      </div>
    </div>
  </div>
</body>
</html>`;
}

const label = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
};

const TIERS = ['1', '2', '3', 'ruled_out'];
const TIER_LABEL = { '1': 'Tier 1', '2': 'Tier 2', '3': 'Tier 3', ruled_out: 'Ruled out' };
const TIER_TONE  = { '1': 'ok', '2': 'blue', '3': 'warn', ruled_out: 'bad' };

const STATUSES = ['researching', 'contacted', 'applied', 'booked', 'attended', 'not_pursuing'];
const STATUS_LABEL = { researching: 'Researching', contacted: 'Contacted', applied: 'Applied', booked: 'Booked', attended: 'Attended', not_pursuing: 'Not pursuing' };
const STATUS_TONE  = { researching: 'warn', contacted: 'blue', applied: 'blue', booked: 'ok', attended: 'ok', not_pursuing: 'bad' };

const CATEGORIES = ['farmers_market', 'street_market', 'festival'];
const CATEGORY_LABEL = { farmers_market: 'Farmers market', street_market: 'Street market', festival: 'Festival' };

const BLANK_MARKET = {
  name: '', category: 'farmers_market', tier: '2',
  location: '', driveTime: '', schedule: '',
  contactName: '', email: '', phone: '', website: '',
  fee: '', requirements: '', whyItFits: '',
};

function Banner({ msg, onClose }) {
  if (!msg) return null;
  const isErr = msg.startsWith('Error');
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 'var(--r-lg)', marginBottom: 16,
      background: isErr ? '#fef2f2' : '#f0fdf4',
      border: `1px solid ${isErr ? '#fca5a5' : '#86efac'}`,
      color: isErr ? '#991b1b' : '#166534',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14,
    }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, lineHeight: 1 }}>×</button>
    </div>
  );
}

function interactionLine(i) {
  switch (i.type) {
    case 'note':          return { title: 'Note', body: i.text };
    case 'email':         return { title: 'Email sent', body: `"${i.subject}" → ${i.to}` };
    case 'status_change': return { title: 'Status changed', body: `${STATUS_LABEL[i.fromStatus] || i.fromStatus} → ${STATUS_LABEL[i.toStatus] || i.toStatus}` };
    default:               return { title: i.type, body: '' };
  }
}

function FieldGroup({ children }) {
  return <div style={{ display: 'grid', gap: 12 }}>{children}</div>;
}

function MarketForm({ form, setForm }) {
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <FieldGroup>
      <div>
        <label style={label}>Name</label>
        <Input value={form.name} onChange={set('name')} autoFocus />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={label}>Category</label>
          <Select value={form.category} onChange={set('category')}>
            {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
          </Select>
        </div>
        <div>
          <label style={label}>Tier</label>
          <Select value={form.tier} onChange={set('tier')}>
            {TIERS.map(t => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
          </Select>
        </div>
      </div>
      <div>
        <label style={label}>Location</label>
        <Input value={form.location} onChange={set('location')} placeholder="Venue, address" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={label}>Drive time</label>
          <Input value={form.driveTime} onChange={set('driveTime')} placeholder="~15–20 min" />
        </div>
        <div>
          <label style={label}>Fee</label>
          <Input value={form.fee} onChange={set('fee')} placeholder="£25/day, TBC, etc." />
        </div>
      </div>
      <div>
        <label style={label}>Schedule</label>
        <Textarea style={{ height: 60 }} value={form.schedule} onChange={set('schedule')} placeholder="When it runs, confirmed dates…" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={label}>Contact name</label>
          <Input value={form.contactName} onChange={set('contactName')} />
        </div>
        <div>
          <label style={label}>Phone</label>
          <Input value={form.phone} onChange={set('phone')} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={label}>Email</label>
          <Input type="email" value={form.email} onChange={set('email')} />
        </div>
        <div>
          <label style={label}>Website</label>
          <Input value={form.website} onChange={set('website')} />
        </div>
      </div>
      <div>
        <label style={label}>Requirements</label>
        <Textarea style={{ height: 70 }} value={form.requirements} onChange={set('requirements')} placeholder="Insurance, pitch size, stall colour, application process…" />
      </div>
      <div>
        <label style={label}>Why it fits</label>
        <Textarea style={{ height: 90 }} value={form.whyItFits} onChange={set('whyItFits')} placeholder="Notes on fit, competition, caveats…" />
      </div>
    </FieldGroup>
  );
}

export default function MarketsTab() {
  const [markets, setMarkets]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [banner, setBanner]     = useState('');
  const flash = msg => { setBanner(msg); setTimeout(() => setBanner(''), 6000); };

  const [selectedId, setSelectedId] = useState(null);
  const selectedMarket = markets.find(m => m._id === selectedId) || null;

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_MARKET);
  const [saving, setSaving]   = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(BLANK_MARKET);
  const [savingEdit, setSavingEdit] = useState(false);

  const [noteText, setNoteText]     = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const [pitchSubject, setPitchSubject] = useState('');
  const [pitchHtml, setPitchHtml]       = useState('');
  const [sendingPitch, setSendingPitch] = useState(false);

  const [featuredIds, setFeaturedIds] = useState([]);
  const pickablePhotos = useFeaturablePhotos();
  const { template: starterTemplate, saving: savingStarter, save: saveStarterTemplateRaw } = useSavedTemplate(MARKET_TEMPLATE_NAME);

  const toggleFeatured = (id) => {
    setFeaturedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_FEATURED_PRODUCTS) {
        flash(`You can feature up to ${MAX_FEATURED_PRODUCTS} products at a time`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const [copiedPhotoId, setCopiedPhotoId] = useState(null);
  const copyPhotoUrl = (e, p) => {
    e.stopPropagation(); // don't also toggle "featured" for this thumbnail
    navigator.clipboard.writeText(p.url).then(() => {
      setCopiedPhotoId(p.id);
      setTimeout(() => setCopiedPhotoId(null), 1500);
    });
  };

  const loadMarkets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/markets');
      setMarkets(data);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMarkets(); }, [loadMarkets]);

  // ── Create ──────────────────────────────────────────────────────────────────
  const createMarket = async () => {
    if (!addForm.name.trim()) return flash('Error: Name is required');
    setSaving(true);
    try {
      await api.post('/markets', addForm);
      await loadMarkets();
      setShowAdd(false);
      setAddForm(BLANK_MARKET);
      flash(`Added "${addForm.name.trim()}"`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────────────
  const openEdit = () => {
    if (!selectedMarket) return;
    const { name, category, tier, location, driveTime, schedule, contactName, email, phone, website, fee, requirements, whyItFits } = selectedMarket;
    setEditForm({ name, category, tier, location, driveTime, schedule, contactName: contactName || '', email: email || '', phone: phone || '', website: website || '', fee: fee || '', requirements: requirements || '', whyItFits: whyItFits || '' });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return flash('Error: Name is required');
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/markets/${selectedMarket._id}`, editForm);
      setMarkets(prev => prev.map(m => m._id === data._id ? data : m));
      setShowEdit(false);
      flash('Market updated');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Tier / status change ─────────────────────────────────────────────────────
  const setTier = async (tier) => {
    if (!selectedMarket || tier === selectedMarket.tier) return;
    try {
      const { data } = await api.patch(`/markets/${selectedMarket._id}`, { tier });
      setMarkets(prev => prev.map(m => m._id === data._id ? data : m));
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  const setStatus = async (status) => {
    if (!selectedMarket || status === selectedMarket.status) return;
    try {
      const { data } = await api.patch(`/markets/${selectedMarket._id}`, { status });
      setMarkets(prev => prev.map(m => m._id === data._id ? data : m));
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const deleteMarket = async () => {
    if (!selectedMarket) return;
    if (!window.confirm(`Delete "${selectedMarket.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/markets/${selectedMarket._id}`);
      setSelectedId(null);
      await loadMarkets();
      flash('Market deleted');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  // ── Note ────────────────────────────────────────────────────────────────────
  const addNote = async () => {
    if (!noteText.trim() || !selectedMarket) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/markets/${selectedMarket._id}/notes`, { text: noteText.trim() });
      setMarkets(prev => prev.map(m => m._id === data._id ? data : m));
      setNoteText('');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSavingNote(false);
    }
  };

  // ── Send pitch email ────────────────────────────────────────────────────────
  const sendPitch = async () => {
    if (!selectedMarket) return;
    if (!selectedMarket.email) return flash('Error: This market has no email address');
    if (!pitchSubject.trim() || !pitchHtml.trim()) return flash('Error: Add a subject and message body first');
    if (!window.confirm(`Send "${pitchSubject.trim()}" to ${selectedMarket.email}?`)) return;
    setSendingPitch(true);
    try {
      const { data } = await api.post(`/markets/${selectedMarket._id}/send-email`, { subject: pitchSubject.trim(), html: pitchHtml, replyTo: REPLY_TO_EMAIL });
      setMarkets(prev => prev.map(m => m._id === data.market._id ? data.market : m));
      setPitchSubject('');
      setPitchHtml('');
      flash(`Sent to ${selectedMarket.email}`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSendingPitch(false);
    }
  };

  const useStarterTemplate = () => {
    if ((pitchSubject.trim() || pitchHtml.trim()) && !window.confirm('Replace the current subject and message with the starter template?')) return;
    if (starterTemplate) {
      setPitchSubject(starterTemplate.subject || '');
      setPitchHtml(starterTemplate.html || '');
      return;
    }
    const photos = featuredIds
      .map(id => pickablePhotos.find(p => p.id === id))
      .filter(Boolean)
      .map(p => ({ url: p.url, alt: p.name }));
    setPitchSubject(DEFAULT_MARKET_SUBJECT);
    setPitchHtml(buildMarketPitchHtml(photos));
  };

  const saveStarterTemplate = async () => {
    if (!pitchSubject.trim() || !pitchHtml.trim()) return flash('Error: Write a subject and message before saving it as the starter template');
    try {
      await saveStarterTemplateRaw(pitchSubject, pitchHtml);
      flash('Saved — "Use starter template" will load this from now on');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  const previewHtml = pitchHtml.replace(/\{\{name\}\}/g, selectedMarket?.contactName || 'there').replace(/\{\{email\}\}/g, selectedMarket?.email || '');

  // ── Derived list ────────────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const visibleMarkets = markets.filter(m => {
    if (tierFilter !== 'all' && m.tier !== tierFilter) return false;
    if (!q) return true;
    return [m.name, m.location, m.contactName].some(v => (v || '').toLowerCase().includes(q));
  });

  const counts = {
    all: markets.length,
    '1': markets.filter(m => m.tier === '1').length,
    '2': markets.filter(m => m.tier === '2').length,
    '3': markets.filter(m => m.tier === '3').length,
    ruled_out: markets.filter(m => m.tier === 'ruled_out').length,
  };

  // ── Detail view ─────────────────────────────────────────────────────────────
  if (selectedMarket) {
    const timeline = [...selectedMarket.interactions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return (
      <div>
        <Banner msg={banner} onClose={() => setBanner('')} />
        <Button variant="ghost" onClick={() => setSelectedId(null)} style={{ marginBottom: 16 }}>← Back to markets</Button>

        <Card style={{ padding: '20px 24px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ margin: '0 0 4px', color: 'var(--brand,#113449)', fontSize: 20 }}>{selectedMarket.name}</h3>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                {CATEGORY_LABEL[selectedMarket.category]}
                {selectedMarket.location && <span> · {selectedMarket.location}</span>}
                {selectedMarket.driveTime && <span> · {selectedMarket.driveTime}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={openEdit}>Edit</Button>
              <Button variant="danger" onClick={deleteMarket}>Delete market</Button>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={label}>Tier</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {TIERS.map(t => (
                <span key={t} onClick={() => setTier(t)} style={{ cursor: 'pointer', opacity: t === selectedMarket.tier ? 1 : 0.45 }}>
                  <Pill tone={TIER_TONE[t]}>{TIER_LABEL[t]}</Pill>
                </span>
              ))}
            </div>
            <label style={label}>Status</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {STATUSES.map(s => (
                <span key={s} onClick={() => setStatus(s)} style={{ cursor: 'pointer', opacity: s === selectedMarket.status ? 1 : 0.45 }}>
                  <Pill tone={STATUS_TONE[s]}>{STATUS_LABEL[s]}</Pill>
                </span>
              ))}
            </div>
          </div>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <Card style={{ padding: '18px 20px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Schedule & contact</h3>
            {selectedMarket.schedule && <p style={{ fontSize: 13, margin: '0 0 10px', whiteSpace: 'pre-wrap' }}>{selectedMarket.schedule}</p>}
            <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
              {selectedMarket.contactName && <div>{selectedMarket.contactName}</div>}
              {selectedMarket.email && <div><a href={`mailto:${selectedMarket.email}`} style={{ color: 'var(--brand-2)' }}>{selectedMarket.email}</a></div>}
              {selectedMarket.phone && <div>{selectedMarket.phone}</div>}
              {selectedMarket.website && <div><a href={/^https?:\/\//i.test(selectedMarket.website) ? selectedMarket.website : `https://${selectedMarket.website}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-2)' }}>{selectedMarket.website}</a></div>}
              {selectedMarket.fee && <div style={{ marginTop: 6 }}><strong>Fee:</strong> {selectedMarket.fee}</div>}
              {!selectedMarket.contactName && !selectedMarket.email && !selectedMarket.phone && !selectedMarket.website && (
                <div style={{ color: 'var(--muted)' }}>No contact details yet.</div>
              )}
            </div>
          </Card>

          <Card style={{ padding: '18px 20px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Requirements</h3>
            <p style={{ fontSize: 13, margin: 0, color: selectedMarket.requirements ? 'var(--text)' : 'var(--muted)', whiteSpace: 'pre-wrap' }}>
              {selectedMarket.requirements || 'Nothing on file yet.'}
            </p>
          </Card>
        </div>

        {selectedMarket.whyItFits && (
          <Card style={{ padding: '18px 20px', marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Why it fits</h3>
            <p style={{ fontSize: 13, margin: 0, whiteSpace: 'pre-wrap' }}>{selectedMarket.whyItFits}</p>
          </Card>
        )}

        {/* Send pitch email */}
        <Card style={{ padding: '18px 20px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>
              Send an email
              {!selectedMarket.email && <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 8, fontSize: 12 }}>(no email address on file)</span>}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>{starterTemplate ? 'using your saved version' : 'using the built-in default'}</span>
              <Button variant="ghost" onClick={useStarterTemplate}>Use starter template</Button>
              <Button variant="ghost" onClick={saveStarterTemplate} disabled={savingStarter || !pitchSubject.trim() || !pitchHtml.trim()} title="Overwrite the starter template with what's currently in the boxes below">
                {savingStarter ? 'Saving…' : 'Save as starter template'}
              </Button>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={label}>Feature products in the template <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional, up to {MAX_FEATURED_PRODUCTS} — or click the link icon to copy a photo's URL)</span></label>
            {pickablePhotos.length === 0 ? (
              <p style={{ color: 'var(--muted)', fontSize: 12, margin: 0 }}>No product photos available to feature — the starter template will use generic sample photos instead.</p>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {pickablePhotos.map(p => {
                  const picked = featuredIds.includes(p.id);
                  return (
                    <div
                      key={p.id}
                      onClick={() => toggleFeatured(p.id)}
                      title={p.name}
                      style={{
                        width: 72, cursor: 'pointer', textAlign: 'center',
                        border: `2px solid ${picked ? 'var(--brand-2)' : 'var(--border)'}`,
                        borderRadius: 'var(--r-md)', padding: 4, background: picked ? 'var(--panel-2)' : 'var(--panel)',
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <img src={p.url} alt="" style={{ width: '100%', height: 48, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                        <button
                          onClick={(e) => copyPhotoUrl(e, p)}
                          title="Copy image link"
                          style={{
                            position: 'absolute', top: 2, right: 2, width: 18, height: 18, padding: 0,
                            border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10, lineHeight: '18px',
                            background: 'rgba(255,255,255,.9)', color: 'var(--brand-2)',
                          }}
                        >
                          {copiedPhotoId === p.id ? '✓' : '🔗'}
                        </button>
                      </div>
                      <div style={{ fontSize: 10, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: picked ? 'var(--brand-2)' : 'var(--muted)', fontWeight: picked ? 700 : 400 }}>
                        {p.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={label}>Subject</label>
              <Input style={{ marginBottom: 10 }} placeholder="e.g. Stallholder enquiry" value={pitchSubject} onChange={e => setPitchSubject(e.target.value)} />
              <label style={label}>Message <span style={{ textTransform: 'none', fontWeight: 400 }}>— use {'{{name}}'}</span></label>
              <Textarea style={{ height: 460, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} value={pitchHtml} onChange={e => setPitchHtml(e.target.value)} placeholder="<p>Hi {{name}}, ...</p>" />
              <Button variant="primary" style={{ marginTop: 10 }} onClick={sendPitch} disabled={sendingPitch || !selectedMarket.email}>
                {sendingPitch ? 'Sending…' : 'Send email'}
              </Button>
            </div>
            <div>
              <label style={label}>Preview</label>
              {pitchHtml ? (
                <iframe title="Pitch preview" srcDoc={previewHtml} sandbox="allow-same-origin" style={{ width: '100%', height: 460, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: '#fff' }} />
              ) : (
                <div style={{ height: 460, border: '1px dashed var(--border)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 13 }}>
                  Preview appears here
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card style={{ padding: '18px 20px', marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Add a note</h3>
          <Textarea style={{ height: 90, fontSize: 13 }} placeholder="What happened, what you found out…" value={noteText} onChange={e => setNoteText(e.target.value)} />
          <Button variant="primary" style={{ marginTop: 10 }} onClick={addNote} disabled={savingNote || !noteText.trim()}>
            {savingNote ? 'Saving…' : 'Add note'}
          </Button>
        </Card>

        <Card style={{ padding: '18px 20px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>History</h3>
          {timeline.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: 13 }}>No activity yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {timeline.map(i => {
                const { title, body } = interactionLine(i);
                return (
                  <div key={i._id} style={{ padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--panel-2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                      <strong style={{ color: 'var(--text)' }}>{title}</strong>
                      <span>{new Date(i.createdAt).toLocaleString('en-GB')}</span>
                    </div>
                    {body && <div style={{ fontSize: 13 }}>{body}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {showEdit && (
          <ModalShell onClose={() => setShowEdit(false)} width={560} ariaLabel="Edit market">
            <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Edit market</h2>
            <MarketForm form={editForm} setForm={setEditForm} />
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <Button variant="muted" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button variant="primary" onClick={saveEdit} disabled={savingEdit || !editForm.name.trim()}>
                {savingEdit ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </ModalShell>
        )}
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  return (
    <div>
      <Banner msg={banner} onClose={() => setBanner('')} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <Input style={{ maxWidth: 280 }} placeholder="Search markets…" value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span onClick={() => setTierFilter('all')} style={{ cursor: 'pointer', opacity: tierFilter === 'all' ? 1 : 0.5 }}>
            <Pill>{`All (${counts.all})`}</Pill>
          </span>
          {TIERS.map(t => (
            <span key={t} onClick={() => setTierFilter(t)} style={{ cursor: 'pointer', opacity: tierFilter === t ? 1 : 0.5 }}>
              <Pill tone={TIER_TONE[t]}>{`${TIER_LABEL[t]} (${counts[t]})`}</Pill>
            </span>
          ))}
        </div>
        <Button variant="primary" style={{ marginLeft: 'auto' }} onClick={() => setShowAdd(true)}>+ Add market</Button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--muted)' }}>Loading…</p>
      ) : visibleMarkets.length === 0 ? (
        <Card style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
          {markets.length === 0 ? 'No markets yet — add your first one above.' : 'No markets match this filter.'}
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleMarkets.map(m => (
            <Card key={m._id} style={{ padding: '12px 16px', cursor: 'pointer' }} onClick={() => setSelectedId(m._id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[CATEGORY_LABEL[m.category], m.location, m.driveTime].filter(Boolean).join(' · ') || 'No details yet'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Pill tone={TIER_TONE[m.tier]}>{TIER_LABEL[m.tier]}</Pill>
                  <Pill tone={STATUS_TONE[m.status]}>{STATUS_LABEL[m.status]}</Pill>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showAdd && (
        <ModalShell onClose={() => setShowAdd(false)} width={560} ariaLabel="Add market">
          <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Add a market</h2>
          <MarketForm form={addForm} setForm={setAddForm} />
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <Button variant="muted" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button variant="primary" onClick={createMarket} disabled={saving || !addForm.name.trim()}>
              {saving ? 'Adding…' : 'Add market'}
            </Button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
